import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { GlassCard, GlassButton } from '../../components/ui/GlassComponents';

// ---------- date helpers ----------

function daysInMonth(year, month /* 1-12 */) {
  return new Date(year, month, 0).getDate();
}

function isWeekend(year, month, day) {
  const dow = new Date(year, month - 1, day).getDay();
  return dow === 0 || dow === 6;
}

function isToday(year, month, day) {
  const d = new Date();
  return d.getFullYear() === year && d.getMonth() + 1 === month && d.getDate() === day;
}

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];
const WEEKDAY_LETTERS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

// ---------- leave-span helpers ----------

function computeLeavePosition(leave, year, month, dim) {
  const monthStart = new Date(Date.UTC(year, month - 1, 1));
  const monthEnd = new Date(Date.UTC(year, month - 1, dim, 23, 59, 59, 999));
  const leaveStart = new Date(leave.startDate);
  const leaveEnd = new Date(leave.endDate);
  if (leaveEnd < monthStart || leaveStart > monthEnd) return null;
  const clampedStart = leaveStart < monthStart ? monthStart : leaveStart;
  const clampedEnd = leaveEnd > monthEnd ? monthEnd : leaveEnd;
  const startDay = clampedStart.getUTCDate();
  const endDay = clampedEnd.getUTCDate();
  // +1 because grid column 1 is the name column.
  return { startCol: startDay + 1, span: (endDay - startDay) + 1 };
}

// ---------- main component ----------

const WhosOut = () => {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [bankHolidaysByDate, setBankHolidaysByDate] = useState({}); // { 'YYYY-MM-DD': 'Good Friday' }
  const [hoveredLeave, setHoveredLeave] = useState(null);

  const dim = useMemo(() => daysInMonth(year, month), [year, month]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get('/api/calendar/month', { params: { year, month } })
      .then(r => {
        if (cancelled) return;
        setUsers(r.data.users || []);
        setLeaves(r.data.leaves || []);
      })
      .catch(err => {
        if (cancelled) return;
        console.error(err);
        toast.error("Failed to load Who's Out");
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [year, month]);

  // Bank holidays are fetched per year and cached in state; silent fail
  // if the endpoint is unavailable (feature degrades gracefully).
  useEffect(() => {
    let cancelled = false;
    axios.get('/api/bank-holidays', { params: { year } })
      .then(r => {
        if (cancelled) return;
        const map = {};
        (r.data?.events || []).forEach(e => { map[e.date] = e.title; });
        setBankHolidaysByDate(map);
      })
      .catch(() => { if (!cancelled) setBankHolidaysByDate({}); });
    return () => { cancelled = true; };
  }, [year]);

  const goPrevMonth = () => {
    if (month === 1) { setMonth(12); setYear(year - 1); }
    else setMonth(month - 1);
  };
  const goNextMonth = () => {
    if (month === 12) { setMonth(1); setYear(year + 1); }
    else setMonth(month + 1);
  };
  const goToday = () => {
    const d = new Date();
    setYear(d.getFullYear());
    setMonth(d.getMonth() + 1);
  };

  const leavesByUser = useMemo(() => {
    const m = new Map();
    for (const l of leaves) {
      if (!m.has(l.userId)) m.set(l.userId, []);
      m.get(l.userId).push(l);
    }
    return m;
  }, [leaves]);

  // Helper: is a given day of the visible month a bank holiday? Returns
  // the title (for tooltip) or null.
  const bankHolidayForDay = (day) => {
    const key = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return bankHolidaysByDate[key] || null;
  };

  // Grid template: 220px for the name column, then one minmax-sized
  // column per day of the visible month.
  const gridStyle = {
    gridTemplateColumns: `220px repeat(${dim}, minmax(32px, 1fr))`
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-gray-900 dark:via-blue-900 dark:to-purple-900 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Who&apos;s Out
            </h1>
            <p className="text-gray-600 dark:text-gray-300 mt-1">
              At-a-glance view of who is off this month
            </p>
          </div>
          <div className="flex items-center gap-2">
            <GlassButton onClick={goPrevMonth} disabled={loading}>Prev Month</GlassButton>
            <button
              onClick={goToday}
              className="px-3 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white/60 dark:bg-gray-800/60 hover:bg-white dark:hover:bg-gray-700"
            >
              Today
            </button>
            <GlassButton onClick={goNextMonth} disabled={loading}>Next Month</GlassButton>
          </div>
        </div>

        <div className="mb-3 text-lg font-semibold text-gray-900 dark:text-white">
          {MONTH_NAMES[month - 1]} {year}
        </div>

        <GlassCard className="p-0 overflow-hidden">
          <div className="overflow-x-auto">
            <div className="grid text-sm" style={gridStyle}>
              {/* ------- Row 1: header ------- */}
              <div
                className="sticky left-0 z-20 bg-gray-50 dark:bg-gray-900/80 backdrop-blur-sm px-4 py-2 border-b border-r border-gray-200 dark:border-gray-700 font-medium text-gray-600 dark:text-gray-300"
                style={{ gridRow: 1, gridColumn: 1 }}
              >
                Team member
              </div>

              {Array.from({ length: dim }, (_, i) => {
                const day = i + 1;
                const weekend = isWeekend(year, month, day);
                const today = isToday(year, month, day);
                const holiday = bankHolidayForDay(day);
                const weekday = new Date(year, month - 1, day).getDay();
                // Bank holidays take precedence over weekends for colour.
                const cellClass = holiday
                  ? 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                  : weekend
                  ? 'bg-gray-100 dark:bg-gray-900/60 text-gray-400'
                  : 'bg-gray-50 dark:bg-gray-900/40 text-gray-600 dark:text-gray-300';
                return (
                  <div
                    key={`hd-${day}`}
                    className={`text-center py-2 border-b border-gray-200 dark:border-gray-700 text-xs font-medium ${cellClass} ${today ? 'ring-2 ring-inset ring-blue-500' : ''}`}
                    style={{ gridRow: 1, gridColumn: day + 1 }}
                    title={holiday || undefined}
                  >
                    <div className="text-[10px] uppercase tracking-wide">{WEEKDAY_LETTERS[weekday]}</div>
                    <div className="text-sm font-semibold">{day}</div>
                    {holiday && (
                      <div className="text-[9px] truncate px-0.5 text-red-700 dark:text-red-300" title={holiday}>
                        {holiday.replace(/[^A-Za-z ]/g, '').split(' ').slice(0, 2).join(' ')}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* ------- Body rows ------- */}
              {loading ? (
                <div className="col-span-full text-center py-12 text-gray-500 dark:text-gray-400"
                     style={{ gridRow: 2, gridColumn: `1 / span ${dim + 1}` }}>
                  Loading…
                </div>
              ) : users.length === 0 ? (
                <div className="col-span-full text-center py-12 text-gray-500 dark:text-gray-400"
                     style={{ gridRow: 2, gridColumn: `1 / span ${dim + 1}` }}>
                  No active team members to display.
                </div>
              ) : users.map((u, rowIndex) => (
                <UserRow
                  key={u.id}
                  user={u}
                  leaves={leavesByUser.get(u.id) || []}
                  year={year}
                  month={month}
                  dim={dim}
                  rowIndex={rowIndex}
                  bankHolidayForDay={bankHolidayForDay}
                  onHoverLeave={setHoveredLeave}
                />
              ))}
            </div>
          </div>
        </GlassCard>

        {/* Legend */}
        <div className="mt-4 flex flex-wrap items-center gap-4 text-xs text-gray-600 dark:text-gray-300">
          <div className="flex items-center gap-1">
            <span className="inline-block w-4 h-3 rounded-sm" style={{ backgroundColor: '#4f46e5' }} />
            Approved leave
          </div>
          <div className="flex items-center gap-1">
            <span className="inline-block w-4 h-3 rounded-sm border border-dashed" style={{
              backgroundImage: 'repeating-linear-gradient(45deg, #fbbf24 0, #fbbf24 3px, #fef3c7 3px, #fef3c7 6px)'
            }} />
            Pending approval
          </div>
          <div className="flex items-center gap-1">
            <span className="inline-block w-4 h-3 rounded-sm bg-red-50 dark:bg-red-900/30 border border-red-300 dark:border-red-700" />
            Bank holiday
          </div>
          <div className="flex items-center gap-1">
            <span className="inline-block w-4 h-3 rounded-sm bg-gray-200 dark:bg-gray-700" />
            Weekend
          </div>
          <div className="flex items-center gap-1">
            <span className="inline-block w-4 h-3 rounded-sm ring-2 ring-inset ring-blue-500" />
            Today
          </div>
        </div>

        {/* Hover tooltip */}
        {hoveredLeave && (
          <div
            className="pointer-events-none fixed z-50 rounded-lg shadow-lg px-3 py-2 bg-gray-900 text-white text-xs max-w-xs"
            style={{ top: hoveredLeave.y + 14, left: hoveredLeave.x + 14 }}
          >
            <div className="font-semibold">{hoveredLeave.userName}</div>
            <div>{hoveredLeave.typeName} · {hoveredLeave.status}</div>
            <div>{formatDateRange(hoveredLeave.startDate, hoveredLeave.endDate)}{hoveredLeave.halfDay ? ' · half-day' : ''}</div>
            {hoveredLeave.notes && <div className="opacity-75 mt-1 italic">{hoveredLeave.notes}</div>}
          </div>
        )}
      </div>
    </div>
  );
};

function formatDateRange(start, end) {
  const s = new Date(start);
  const e = new Date(end);
  const fmt = d => d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  return s.toDateString() === e.toDateString() ? fmt(s) : `${fmt(s)} – ${fmt(e)}`;
}

const UserRow = ({ user, leaves, year, month, dim, rowIndex, bankHolidayForDay, onHoverLeave }) => {
  // Actual grid row this user occupies. Header = row 1, so user N = row N+2.
  const gridRow = rowIndex + 2;
  const rowBg = rowIndex % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50/60 dark:bg-gray-900/40';

  return (
    <React.Fragment>
      {/* Name cell — explicitly placed at (gridRow, col 1). */}
      <div
        className={`sticky left-0 z-10 px-3 py-2 border-b border-r border-gray-200 dark:border-gray-700 flex items-center gap-2 ${rowBg}`}
        style={{ gridRow, gridColumn: 1 }}
      >
        {user.avatar ? (
          <img src={user.avatar} alt="" className="w-7 h-7 rounded-full object-cover flex-shrink-0" />
        ) : (
          <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 text-white text-xs flex items-center justify-center flex-shrink-0 font-semibold">
            {user.firstName?.[0]}{user.lastName?.[0]}
          </div>
        )}
        <div className="min-w-0">
          <div className="truncate text-sm font-medium text-gray-900 dark:text-white">
            {user.firstName} {user.lastName}
          </div>
          {user.jobTitle && (
            <div className="truncate text-xs text-gray-500 dark:text-gray-400">{user.jobTitle}</div>
          )}
        </div>
      </div>

      {/* One day cell per day, each explicitly placed at (gridRow, day+1). */}
      {Array.from({ length: dim }, (_, i) => {
        const day = i + 1;
        const weekend = isWeekend(year, month, day);
        const today = isToday(year, month, day);
        const holiday = bankHolidayForDay(day);
        const bgClass = holiday
          ? 'bg-red-50/60 dark:bg-red-900/20'
          : weekend
          ? 'bg-gray-100/70 dark:bg-gray-900/40'
          : rowBg;
        return (
          <div
            key={`u${user.id}-d${day}`}
            className={`relative border-b border-gray-200 dark:border-gray-700 h-12 ${bgClass} ${today ? 'ring-2 ring-inset ring-blue-500/40' : ''}`}
            style={{ gridRow, gridColumn: day + 1 }}
            title={holiday || undefined}
          />
        );
      })}

      {/* Leave bars — overlaid on top of day cells within the same row. */}
      {leaves.map(leave => {
        const pos = computeLeavePosition(leave, year, month, dim);
        if (!pos) return null;
        const isPending = leave.status === 'pending';
        const color = leave.typeColor || '#4f46e5';
        const background = isPending
          ? `repeating-linear-gradient(45deg, ${color} 0, ${color} 4px, rgba(255,255,255,0.6) 4px, rgba(255,255,255,0.6) 8px)`
          : color;
        return (
          <div
            key={`leave-${leave.id}`}
            className={`relative mx-0.5 my-2 rounded-md text-[11px] font-medium text-white flex items-center justify-center cursor-default overflow-hidden ${isPending ? 'border border-dashed border-white/50' : ''}`}
            style={{
              gridRow,
              gridColumn: `${pos.startCol} / span ${pos.span}`,
              background,
              zIndex: 5,
              alignSelf: 'stretch'
            }}
            onMouseEnter={(e) => onHoverLeave({
              x: e.clientX, y: e.clientY,
              userName: `${user.firstName} ${user.lastName}`,
              typeName: leave.typeName || 'Leave',
              status: leave.status,
              startDate: leave.startDate, endDate: leave.endDate,
              halfDay: leave.halfDay, notes: leave.notes
            })}
            onMouseMove={(e) => onHoverLeave({
              x: e.clientX, y: e.clientY,
              userName: `${user.firstName} ${user.lastName}`,
              typeName: leave.typeName || 'Leave',
              status: leave.status,
              startDate: leave.startDate, endDate: leave.endDate,
              halfDay: leave.halfDay, notes: leave.notes
            })}
            onMouseLeave={() => onHoverLeave(null)}
          >
            <span className="truncate px-1">{leave.typeName}</span>
          </div>
        );
      })}
    </React.Fragment>
  );
};

export default WhosOut;
